import { FileText, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type {
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisRunView,
} from "@/features/ai/ai.types";
import { StructuralAnalysisCalloutCard } from "./structural-analysis-callout-card";

type StructuralAnalysisReportProps = {
  deleteBusy?: boolean;
  onDeleteRun?: (runId: string) => void;
  onOpenChapter?: (
    callout: StructuralAnalysisRunView["sections"][number]["findings"][number],
  ) => void;
  onSetReviewState: (
    runId: string,
    dimensionName: StructuralAnalysisRunView["sections"][number]["dimension"],
    findingIndex: number,
    reviewState: StructuralAnalysisCalloutReviewState,
  ) => void;
  run: StructuralAnalysisRunView | null;
  statusBusyId?: string | null;
  storyTitle: string;
};

export function StructuralAnalysisReport({
  deleteBusy = false,
  onDeleteRun,
  onOpenChapter,
  onSetReviewState,
  run,
  statusBusyId = null,
  storyTitle,
}: StructuralAnalysisReportProps) {
  const { t } = useTranslation();

  if (!run) {
    return (
      <div
        className="border-y border-dashed border-[#d9cdbc] py-10 text-sm text-muted-foreground"
        data-testid="structural-analysis-report-empty"
      >
        {t("story.ai.structural.report.empty")}
      </div>
    );
  }

  const totalCallouts = run.sections.reduce(
    (count, section) => count + section.findings.length,
    0,
  );

  return (
    <article
      aria-label={t("story.ai.structural.report.ariaLabel")}
      className="space-y-8"
    >
      <header className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              {t("story.ai.structural.title")}
            </div>
            <h3 className="text-3xl text-foreground">{storyTitle}</h3>
            <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
              {t("story.ai.structural.report.intro")}
            </p>
          </div>
          <div className="min-w-[15rem] space-y-3 border-l border-[#e4d6c6] pl-5 text-sm text-muted-foreground">
            <dl className="space-y-2">
              <div>
                <dt className="font-semibold text-foreground">
                  {t("story.ai.structural.report.scopeLabel")}
                </dt>
                <dd>{run.scopeLabelSnapshot}</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">
                  {t("story.ai.structural.report.createdLabel")}
                </dt>
                <dd>{new Date(run.createdAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">
                  {t("story.ai.structural.report.creditsLabel")}
                </dt>
                <dd>{run.creditsUsed}</dd>
              </div>
            </dl>
            {onDeleteRun ? (
              <Button
                disabled={deleteBusy}
                onClick={() => onDeleteRun(run.id)}
                size="sm"
                type="button"
                variant="outline"
              >
                <Trash2 className="h-4 w-4" />
                {deleteBusy
                  ? t("story.ai.structural.report.deleting")
                  : t("story.ai.structural.report.deleteAction")}
              </Button>
            ) : null}
          </div>
        </div>

        {totalCallouts === 0 ? (
          <div className="border-y border-dashed border-[#d9cdbc] py-4 text-sm text-muted-foreground">
            {t("story.ai.structural.report.noFindings")}
          </div>
        ) : null}
      </header>

      <div className="space-y-8">
        {run.sections.map((section) => (
          <section className="space-y-4 border-t border-[#ece2d6] pt-8" key={section.id}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="text-xl font-semibold text-foreground">
                {section.dimensionLabel}
              </h4>
              <span className="rounded-full border border-[#e4d6c6] bg-[#fbf6ee] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t("story.ai.structural.report.findingCount", {
                  count: section.findings.length,
                })}
              </span>
            </div>

            {section.findings.length ? (
              <div className="divide-y divide-[#ece2d6] border-y border-[#ece2d6]">
                {section.findings.map((callout) => (
                  <StructuralAnalysisCalloutCard
                    busy={statusBusyId === callout.id}
                    callout={callout}
                    key={callout.id}
                    onOpenChapter={onOpenChapter}
                    onSetReviewState={(reviewState) =>
                      onSetReviewState(
                        callout.runId,
                        callout.dimension,
                        callout.findingIndex,
                        reviewState,
                      )}
                  />
                ))}
              </div>
            ) : (
              <div className="border-y border-dashed border-[#d9cdbc] py-4 text-sm text-muted-foreground">
                {t("story.ai.structural.report.emptyDimension")}
              </div>
            )}
          </section>
        ))}
      </div>
    </article>
  );
}
